import { Component, inject, signal, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NotificationToastComponent } from './components/common/notification-toast.component';
import { NotificationService } from './shared/services/notification.service';
import { AuthStateService } from './shared/states/auth.state';
import { SocketService } from './shared/services/socket.service';
import { CallService } from './shared/services/call.service';
import { ChatService } from './shared/services/chat.service';
import { CallOverlay } from './components/call/call-overlay';
import { distinctUntilChanged } from 'rxjs';
import { ConfirmationPopup } from './components/ui/confirmation-popup/confirmation-popup';
import { LoginPromptService } from './shared/services/login-prompt.service';
import { userNeedsAgeVerification } from './shared/services/age-verification.service';
import { AuthService } from './shared/services/auth.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NotificationToastComponent, CallOverlay, ConfirmationPopup],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnInit {
  protected readonly title = signal('safeword-ssr-user');

  private notificationService = inject(NotificationService);
  private authState           = inject(AuthStateService);
  private socket               = inject(SocketService);
  private callService          = inject(CallService);
  private chatService          = inject(ChatService);
  private router               = inject(Router);
  protected loginPrompt        = inject(LoginPromptService);
  private authService          = inject(AuthService);

  /** @userName of the profile an admin opened via "Quick Login", else null. */
  protected quickLoginUser     = signal<string | null>(null);
  protected leavingQuickLogin  = signal(false);

  /** Sign out of the quick-login session and go back to the admin panel. */
  backToAdmin(): void {
    if (this.leavingQuickLogin()) return;
    this.leavingQuickLogin.set(true);
    const clientId = this.authState.getClientId();
    const redirectUrl = this.authState.getLogoutRedirectUrl();
    const finish = () => {
      this.authState.clearAuth();
      if (typeof window !== 'undefined') window.location.href = redirectUrl;
    };
    this.authService.logout(clientId ?? undefined).subscribe({ next: finish, error: finish });
  }

  goToLogin(): void {
    this.loginPrompt.close();
    this.router.navigate(['/login']);
  }

  ngOnInit(): void {
    this.authState.auth$.subscribe(auth => {
      this.quickLoginUser.set(
        auth?.isLoggedIn && auth?.quickLogin ? (auth.user?.userName ?? auth.user?.displayName ?? 'user') : null
      );
    });

    // Real-time features (notifications, chat, calls) only start once the
    // user has passed age verification — the server rejects the socket
    // otherwise. Re-evaluated when verification completes (same token).
    this.authState.auth$
      .pipe(distinctUntilChanged((a, b) =>
        a?.token === b?.token
        && userNeedsAgeVerification(a?.user) === userNeedsAgeVerification(b?.user)))
      .subscribe(auth => {
        if (auth?.isLoggedIn && auth?.token && !userNeedsAgeVerification(auth.user)) {
          this.notificationService.connect(auth.token);
          this.notificationService.fetchUnreadCount();

          // Previously the call socket only connected once the user opened
          // the chat page (or the profile-sidebar chat widget), which meant
          // an incoming call from another user produced no ring, no banner,
          // nothing — unless the recipient already happened to be on the
          // chat screen. Connecting here means call signalling is live app-
          // wide the moment the user is logged in, on every page.
          this.socket.connect();
          this.callService.initSignalling();

          // Same class of bug for chat: ChatService.init() (which loads the
          // conversation list and starts the message/unread socket
          // listeners that drive totalUnread$) previously only ran once the
          // user opened the Chat page or the profile-sidebar message
          // widget. Until then the header's unread badge was stuck at
          // whatever it defaulted to, which is why it looked "wrong until
          // refresh" on every other screen right after login. Init it here
          // too so the badge (and both places it's shown in the header) is
          // correct app-wide from the moment the user is authenticated.
          this.chatService.init();
        } else {
          this.notificationService.disconnect();
          this.socket.disconnect();
          this.chatService.teardown();
        }
      });
  }
}